<template>
  <svg :x="x" :y="y" :width="`${widthInInches}in`" :height="`${heightInInches}in`" xmlns="http://www.w3.org/2000/svg" :index="matIndex" :key="matKey">
    <defs>
      <mask :id="`${uuid}-mat-mask-${matIndex}`">
        <rect width="100%" height="100%" fill="white"/>
        <rect
          v-for="artworkOpening in artworkOpenings"
          :key="artworkOpening.key"
          :x="artworkOpening.x"
          :y="artworkOpening.y"
          :width="artworkOpening.width"
          :height="artworkOpening.height"
          fill="black"
        />
      </mask>

      <pattern
        v-if="fullMat.surfaceTextureUrl"
        :x="surfaceTexturePatternOrigin.x"
        :y="surfaceTexturePatternOrigin.y"
        :id="`${uuid}-mat-tile-pattern-${matIndex}`"
        patternUnits="userSpaceOnUse"
        :width="fullMat.surfaceTextureWidthInInches"
        :height="fullMat.surfaceTextureHeightInInches"
      >
        <image
          :href="fullMat.surfaceTextureUrl"
          :width="fullMat.surfaceTextureWidthInInches"
          :height="fullMat.surfaceTextureHeightInInches"
          preserveAspectRatio="xMaxYMin"
        />
      </pattern>
    </defs>
    <rect
      :width="matWidth"
      :height="matHeight"
      :fill="matColor"
      :mask="`url(#${uuid}-mat-mask-${matIndex})`"
    />
    <image v-if="matImage" :href="matImage" :width="matWidth" :height="matHeight"/>
    <template v-else>
      <rect
        v-for="artworkOpening in artworkOpenings"
        :key="artworkOpening.key"
        :x="artworkOpening.bevelX"
        :y="artworkOpening.bevelY"
        :width="artworkOpening.bevelWidth"
        :height="artworkOpening.bevelHeight"
        :stroke="coreColor"
        :stroke-width="bevelWidth"
        fill="none"
      />
    </template>
    <RenderSvgMatCaption
      v-if="matCaption"
      :frameSpec="frameSpec"
      :captionX="captionX"
      :captionY="captionY"
      :captionWidth="captionWidth"
    />
    <RenderSvgMatDecoration
      v-if="matIndex === frameSpec.mats.length - 1"
      :frameSpec="frameSpec"
      :artworkOpenings="artworkOpenings"
      :matWidth="matWidth"
      :matHeight="matHeight"
      :matMaskId="`${uuid}-mat-mask-${matIndex}`"
    />
    <rect
      v-if="hasPlaque"
      :stroke="coreColor"
      :stroke-width="bevelWidth"
      :x="plaqueX"
      :y="plaqueY"
      :width="plaqueWidth"
      :height="plaqueHeight"
      fill="none"
    />
    <RenderSvgPlaque
      v-if="hasPlaque"
      :frameSpec="frameSpec"
      :plaqueY="plaqueY + bevelWidth/2"
      :plaqueX="plaqueX + bevelWidth/2"
      :plaqueWidth="plaqueWidth - bevelWidth"
      :plaqueHeight="plaqueHeight - bevelWidth"
    />  
  </svg>
</template>

<script>
import uuid from 'uuid/v4';
import { defaultMatThickness, uploadedArtworkMatOverlap, getMatCaptionOffsetDown } from "../../hardCodedValues";
import RenderSvgMatCaption from './RenderSvgMatCaption.vue';
import RenderSvgMatDecoration from './RenderSvgMatDecoration.vue';
import RenderSvgPlaque from './RenderSvgPlaque.vue';

export default {
  components: {
    RenderSvgMatCaption,
    RenderSvgMatDecoration,
    RenderSvgPlaque
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    mat: {
      type: Object,
      required: true
    },
    matIndex: {
      type: Number,
      required: true
    },
  },
  data() {
    return {
      uuid: uuid()
    };
  },
  computed: {
    fullMat () {
      return this.frameSpec.getFullMat(this.mat.name);
    },
    matKey() {
      return `${this.mat.name}-${this.matIndex}`;
    },
    artwork () {
      return this.frameSpec.getArtwork(0);
    },
    artworks () {
      return this.frameSpec.artworks;
    },
    invertedMats () {
      return this.frameSpec.mats.slice().reverse();
    },
    totalsOfAllMatReveals() {
      return {
        topInInches: this.invertedMats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
        rightInInches: this.invertedMats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
        bottomInInches: this.invertedMats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
        leftInInches: this.invertedMats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
      };
    },
    totalsOfAllMatRevealsBelowThisMat () {
      return {
        topInInches: this.invertedMats.slice(0, this.matIndex).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
        rightInInches: this.invertedMats.slice(0, this.matIndex).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
        bottomInInches: this.invertedMats.slice(0, this.matIndex).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
        leftInInches: this.invertedMats.slice(0, this.matIndex).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
      };
    },
    totalsOfAllMatRevealsThisMatAndAbove () {
      const length = this.invertedMats.length;
      return {
        topInInches: this.invertedMats.slice(this.matIndex, length).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
        rightInInches: this.invertedMats.slice(this.matIndex, length).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
        bottomInInches: this.invertedMats.slice(this.matIndex, length).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
        leftInInches: this.invertedMats.slice(this.matIndex, length).reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
      };
    },
    matImage () {
      if (this.fullMat && this.fullMat.matAsset) {
        // Multi-opening mats (duets, trios, etc) can render via SVG
        // using surfaceColor/surfaceTextureUrl + mask cutouts + bevels, so skip
        // the image overlay. 
        // We will want to move off the mat asset for heartstagram (and potentially other shapes) next
        if (this.fullMat.openings && this.fullMat.openings.length > 1) {
          return null;
        }
        return this.fullMat.matAsset;
      }
      return null;
    },
    matColor() {
      if (!this.fullMat) {
        return 'white';
      } else if (this.fullMat.surfaceTextureUrl) {
        return `url(#${this.uuid}-mat-tile-pattern-${this.matIndex})`;
      } else {
        return this.fullMat.surfaceColor;
      }
    },
    surfaceTexturePatternOrigin () {
      if (!this.fullMat.surfaceTextureWidthInInches || !this.fullMat.surfaceTextureHeightInInches) {
        return {
          x: '0',
          y: '0'
        };
      }

      return {
        x: (this.widthInInches - this.fullMat.surfaceTextureWidthInInches) / 2,
        y: (this.heightInInches - this.fullMat.surfaceTextureHeightInInches) / 2
      };
    },
    coreColor() {
      return this.fullMat ? this.fullMat.coreColors[0] : 'black';
    },
    bevelWidth() {
      return defaultMatThickness; // 1/16th of an inch
    },
    widthInInches() {
      if (this.fullMat && this.fullMat.matWidth) {
        return `${this.fullMat.matWidth}`;
      }
      if (!this.artwork) return '0';

      return `${(this.totalsOfAllMatReveals.leftInInches + this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatReveals.rightInInches)}`;
    },
    heightInInches() {
      if (this.fullMat && this.fullMat.matHeight) {
        return `${this.fullMat.matHeight}`;
      }
      if (!this.artwork) return '0';

      return `${(this.totalsOfAllMatReveals.topInInches + this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatReveals.bottomInInches)}`;
    },
    matWidth() {
      if (this.fullMat && this.fullMat.matWidth) {
        return this.fullMat.matWidth;
      }
      if (!this.artwork) return 0;

      return this.totalsOfAllMatReveals.leftInInches + this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatReveals.rightInInches; // In inches
    },
    matHeight() {
      if (this.fullMat && this.fullMat.matHeight) {
        return this.fullMat.matHeight;
      }
      if (!this.artwork) return 0;

      return this.totalsOfAllMatReveals.topInInches + this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatReveals.bottomInInches; // In inches
    },
    firstArtworkOpeningX() {
      if (this.matImage || this.hasMultipleOpenings) {
        return this.totalsOfAllMatRevealsThisMatAndAbove.leftInInches + (uploadedArtworkMatOverlap / 2);
      }
      return this.totalsOfAllMatRevealsThisMatAndAbove.leftInInches;
    },
    firstArtworkOpeningY() {
      if (this.matImage || this.hasMultipleOpenings) {
        return this.totalsOfAllMatRevealsThisMatAndAbove.topInInches + (uploadedArtworkMatOverlap / 2);
      }
      return this.totalsOfAllMatRevealsThisMatAndAbove.topInInches;
    },
    hasMultipleOpenings() {
      return this.fullMat && this.fullMat.openings && this.fullMat.openings.length > 1;
    },
    artworkOpenings() {
      // multi-opening mats - use the mat's openings to calculate the openings
      // there's 1 artwork so we can't use artwork dims here
      if (this.hasMultipleOpenings) {
        return this.fullMat.openings.map((opening, index) => {
          const x = this.firstArtworkOpeningX + opening.leftInInches;
          const y = this.firstArtworkOpeningY + opening.topInInches;
          const width = opening.widthInInches;
          const height = opening.heightInInches;

          return {
            key: index,
            x,
            y,
            width,
            height,
            bevelX: x - (this.bevelWidth / 2),
            bevelY: y - (this.bevelWidth / 2),
            bevelWidth: width + this.bevelWidth,
            bevelHeight: height + this.bevelWidth,
          };
        });
      }

      return this.frameSpec.artworks.map((artwork, artworkKey) => {
        // This defines the rectangular cutout mask where artwork is visible through the mat.
        // Mat images with shaped opening (heartstagram), the image itself handles the opening, so the visible portion should be the whole mat.
        if (this.matImage && (!this.fullMat.openings || this.fullMat.openings.length <= 1)) {
          return {
            key: artworkKey,
            x: 0,
            y: 0,
            width: this.matWidth,
            height: this.matHeight,
            bevelX: 0,
            bevelY: 0,
            bevelWidth: this.matWidth,
            bevelHeight: this.matHeight,
          };
        }

        const x = this.firstArtworkOpeningX;
        const y = this.firstArtworkOpeningY;
        const width = !artwork ? 0
          : this.totalsOfAllMatRevealsBelowThisMat.leftInInches + artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatRevealsBelowThisMat.rightInInches;
        const height = !artwork ? 0
          :this.totalsOfAllMatRevealsBelowThisMat.topInInches + artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatRevealsBelowThisMat.bottomInInches;

        return {
          key: artworkKey,
          x,
          y,
          width,
          height,
          bevelX: x - (this.bevelWidth / 2),
          bevelY: y - (this.bevelWidth / 2),
          bevelWidth: width + this.bevelWidth,
          bevelHeight: height + this.bevelWidth,
        };
      });
    },
    matCaption() {
      return this.mat.matCaption || null;
    },
    captionX() {
      return this.firstArtworkOpeningX;
    },
    captionY() {
      const bottomReveal = this.totalsOfAllMatRevealsThisMatAndAbove.bottomInInches;
      const offsetDown = getMatCaptionOffsetDown(bottomReveal);
      return this.matHeight - bottomReveal + offsetDown;
    },
    captionWidth() {
      return this.matWidth - this.totalsOfAllMatReveals.leftInInches - this.totalsOfAllMatReveals.rightInInches - (this.matImage ? uploadedArtworkMatOverlap : 0);
    },
    hasPlaque() {
      // todo: ensure this checks the actual value on the frameSpec
      return this.frameSpec.matPlaque || this.frameSpec.isAutoMatPlaque();
    },
    plaqueWidth() {
      // note: these are not being handled like a true artwork opening/overlap. 
      // for now this number represents the plaque opening for rendering purposes. if/when we move to custom with a true second opening, we can adjust.
      // eventually we will have small vs large sizes as well
      const smallPlaqueWidth = 3.5;  // Fixed width for small plaque
      return smallPlaqueWidth;
    },
    plaqueHeight() {
      // note: these are not being handled like a true artwork opening/overlap. 
      // for now this number represents the plaque opening for rendering purposes. if/when we move to custom with a true second opening, we can adjust.
      // eventually we will have small vs large sizes as well
      const smallPlaqueHeight = 2.0;  // Fixed height for small plaque
      return smallPlaqueHeight;
    },
    plaqueY() {
      const sizeGroup = this.frameSpec.sizeGroup();

      // we were supposed to have different distances for small vs large plaque, it looks like 
      // the first products are setting the distance based on the art size

      const smallPlaqueOffset = 0.75; // 0.75 inches below the artwork opening
      const largePlaqueOffset = 1; // 1 inch below the artwork opening

      let plaqueOffset = smallPlaqueOffset;
      if (sizeGroup && sizeGroup.name !== "XS") {
        plaqueOffset = largePlaqueOffset;
      }

      return this.matHeight - this.totalsOfAllMatRevealsThisMatAndAbove.bottomInInches + plaqueOffset + (this.bevelWidth / 2);
    },
    plaqueX() {
      // from the left opening, subtract half of the opening, and half of the plaque width to center
      const baseMatOpeningWidth = this.captionWidth;
      return this.firstArtworkOpeningX + (baseMatOpeningWidth / 2) - (this.plaqueWidth / 2);
    }
  }
};
</script>

<style lang="scss" scoped>
svg {
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  max-width: 100%;
  pointer-events: none;
  width: 100%;

  rect {
    pointer-events: none;
  }
}
</style>